import {
    CLEAR_PROMOTED_SEARCH_RESULTS,
    LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
    LOAD_PROMOTED_SEARCH_RESULTS_REQUEST,
    LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
    LOAD_SIMILAR_ITEMS_V3_SUCCESS,
} from './actions';
import api from '@/redux/api/search';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getUserExcludedFacets } from './searchExclusions';
import { GlobalState } from '../rootReducer';

// reducer
const DEFAULT_STATE = {
    all: [],
    itemIds: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let itemIDs;
    switch (action.type) {
        case LOAD_SIMILAR_ITEMS_V3_SUCCESS:
            const itemIds = action.payload.filter(({ itemId, promoted }) => promoted && itemId);
            return {
                ...state,
                // @ts-ignore
                all: [...new Set([...state.all, ...itemIds])], // prevent duplicates
            };
        case CLEAR_PROMOTED_SEARCH_RESULTS:
        case LOAD_PROMOTED_SEARCH_RESULTS_FAIL:
            return DEFAULT_STATE;
        case LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS:
            itemIDs = action.payload.itemIDs || [];
            return {
                ...state,
                // @ts-ignore
                all: [...new Set([...state.all, ...itemIDs])], // prevent duplicates
                itemIds: itemIDs,
            };
        case LOAD_PROMOTED_SEARCH_RESULTS_REQUEST:
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchPromoted;
const idSelector = (state: GlobalState, id) => id;

export const getPromotedSearchResults = createSelector(stateSelector, (state) => state.itemIds || []);
const getAllPromotedSearchResults = createSelector(stateSelector, (state) => state.all || []);

export const isItemPromoted: (state: GlobalState, itemId: number) => any = createSelector(
    [idSelector, getAllPromotedSearchResults],
    (itemId, itemIds) => {
        return itemIds.includes(itemId);
    }
);

/* ACTION CREATORS */
export const clearPromotedSearchResults = () => ({
    payload: { itemIDs: [] },
    type: CLEAR_PROMOTED_SEARCH_RESULTS,
});

export const searchPromoted = ({ ids, pageName, searchQuery }: any) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);

        if (pageName === 'catalog' || pageName === 'price-guide' || pageName === 'sold-search') {
            return dispatch(clearPromotedSearchResults());
        }
        dispatch({
            payload: searchQuery,
            type: LOAD_PROMOTED_SEARCH_RESULTS_REQUEST,
        });

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedOptions = !searchExclusions
            ? searchQuery.options
            : Object.keys(searchExclusions).reduce(
                  (options, facetWithExclusions) => ({
                      ...options,
                      [facetWithExclusions]: [
                          {
                              exclude: searchExclusions[facetWithExclusions] || [],
                              include: (searchQuery.options && searchQuery.options[facetWithExclusions]) || [],
                          },
                      ],
                  }),
                  { ...searchQuery.options }
              );

        const augmentedSearchQuery = {
            ...searchQuery,
            options: augmentedOptions,
        };

        const results = await api.fetchPromotedSearchResults({
            authToken,
            deployment,
            ids,
            searchQuery: augmentedSearchQuery,
        });
        if (results.error) {
            dispatch({
                error: true,
                meta: { augmentedSearchQuery },
                payload: results.payload,
                type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
            });
            return dispatch(clearPromotedSearchResults());
        } else {
            return dispatch({
                payload: results.payload,
                type: LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { searchQuery },
            payload: error,
            type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
        });
        return dispatch(clearPromotedSearchResults());
    }
};
